/**
 * Iter266 — RadioRedirectGuard
 *
 * Guarda client-side para as URLs de rádio removidas do canal
 * "República em Cena" (5 episódios de áudio duplicados dos vídeos
 * do YouTube). Fica como *fallback* do 301 REAL que roda em
 * Cloudflare Bulk Redirects (server-side, ver
 * `backend/scripts/export_cloudflare_bulk_redirects.py`).
 *
 * Fluxo:
 *   1. No mount, busca `/api/redirects/radio-to-video.json` uma única
 *      vez e mantém o mapa em `sessionStorage` (chave `jt_radio_redirects`)
 *      por 30 min para evitar refetch entre navegações.
 *   2. Quando o `pathname` atual bate com o padrão
 *      `/radio/republica-em-cena/<slug>`, procura o slug no mapa. Se
 *      achar, faz `navigate(to_path, { replace: true })` — não deixa
 *      a URL antiga no histórico de navegação.
 *   3. Se não achar match, apenas segue — a página normal do rádio
 *      renderiza (ou o backend devolve 404 se o episódio foi de fato
 *      apagado, o que aciona a página "Episódio não encontrado" do
 *      SPA).
 *
 * Por que não usar `<Route>` estático com `<Navigate>` para cada
 * slug? Porque o mapa é dinâmico — o editor edita
 * `backend/config/radio_video_redirects.json` sem redeploy. Guardar
 * em código exigiria rebuild.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const CACHE_KEY = "jt_radio_redirects_v2";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min

function loadFromCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || !parsed?.data) return null;
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function saveToCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* quota exceeded / private mode — não é fatal */
  }
}

/**
 * Retorna { prefix, episodeSlug } se a URL bater com QUALQUER dos
 * prefixos configurados (`/radio/republica-em-cena/<slug>` OU
 * `/radio/episodes/<slug>`). Retorna null caso contrário.
 */
function parseRadioPath(pathname, sourcePrefixes) {
  for (const prefix of sourcePrefixes) {
    // Regex: prefix + '/' + slug (sem barras adicionais nem query).
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const rx = new RegExp(`^${escaped}/([^/?#]+)/?$`);
    const m = pathname.match(rx);
    if (m) return { prefix, episodeSlug: m[1] };
  }
  return null;
}

export default function RadioRedirectGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(() => loadFromCache());

  // Fetch map on first mount if not cached.
  useEffect(() => {
    if (cfg !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${base}/api/redirects/radio-to-video.json`, {
          credentials: "omit",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const payload = {
          pairs: Array.isArray(data?.pairs) ? data.pairs : [],
          sourcePrefixes: Array.isArray(data?.source_prefixes) ? data.source_prefixes : [],
        };
        if (cancelled) return;
        setCfg(payload);
        saveToCache(payload);
      } catch {
        // Falha silenciosa — se o backend estiver offline, deixa o
        // fluxo normal (usuário vê "não encontrado" se aplicável).
        if (!cancelled) setCfg({ pairs: [], sourcePrefixes: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cfg]);

  // React to pathname changes.
  useEffect(() => {
    if (!cfg || !cfg.pairs?.length || !cfg.sourcePrefixes?.length) return;
    const parsed = parseRadioPath(location.pathname, cfg.sourcePrefixes);
    if (!parsed) return;

    const hit = cfg.pairs.find((p) => p.from_slug === parsed.episodeSlug);
    if (!hit || !hit.to_path) return;

    // Preserva query string original (ex.: ?utm_source=twitter).
    const preserved = location.search || "";
    const target = `${hit.to_path}${preserved}`;

    // `replace: true` — não deixa a URL removida no histórico.
    navigate(target, { replace: true });
  }, [location.pathname, location.search, cfg, navigate]);

  return null;
}
